'use client';

import { closestCenter, DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useCallback, useEffect, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { trpc } from '~/api/trpc';
import { SortableStep } from '~/components/appointments/SortableStep';
import { useAppointmentsCalendarStore } from '~/stores/appointments-calendar-store';
import { Step, useCreateOrModifyAppointmentStore } from '~/stores/create-appointment-store';
import { useUserStore } from '~/stores/user-store';

export const StepList = () => {
    const { steps, setSteps, refreshSelectableResources } = useCreateOrModifyAppointmentStore(
        useShallow(state => ({
            steps: state.steps,
            setSteps: state.setSteps,
            refreshSelectableResources: state.refreshSelectableResources,
        }))
    );

    const { selectedStore } = useAppointmentsCalendarStore(
        useShallow((state) => ({
            selectedStore: state.selectedStore,
        }))
    );

    const { timezone } = useUserStore(
        useShallow(state => ({
            timezone: state.user?.timezone || 'Europe/Berlin',
        }))
    )

    const trpcUtils = trpc.useUtils();

    const recalculateStepTimes = useCallback((currentSteps: Step[]): Step[] => {
        let cumulativeMinutes = 0;
        return currentSteps.map(step => {
            const duration = step.minuteEnd - step.minuteStart; // Preserve original duration intention
            const newMinuteStart = cumulativeMinutes;
            const newMinuteEnd = newMinuteStart + duration;
            cumulativeMinutes = newMinuteEnd; // Update for the next step
            return {
                ...step,
                minuteStart: newMinuteStart,
                minuteEnd: newMinuteEnd,
            };
        });
    }, []);

    // --- Effect to recalculate times when steps array reference changes ---
    // This handles initial load and additions/deletions if they replace the array
    useEffect(() => {
        // Check if times are already sequential to avoid infinite loops if setSteps triggers this
        let isSequential = true;
        let expectedStart = 0;
        for (const step of steps) {
            if (step.minuteStart !== expectedStart) {
                isSequential = false;
                break;
            }
            expectedStart = step.minuteEnd;
        }

        if (!isSequential && steps.length > 0) {
            console.log("Recalculating times due to step array change...");
            setSteps(recalculateStepTimes(steps));

            if(selectedStore?.id) {
                refreshSelectableResources(selectedStore.id, timezone, trpcUtils);
            }
        }
        // Intentionally only run when the steps *array reference* changes,
        // not on every deep change. Recalculation on order/duration change
        // is handled explicitly in their handlers.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [steps, recalculateStepTimes, setSteps]);


    // --- Dnd Setup ---
    // const snapToGrid = useMemo(() => createSnapModifier(GRID_PIXELS_PER_MINUTE), []); // Less relevant now
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8, // Adjust sensitivity as needed
            },
        })
    );

    // Get step IDs for SortableContext
    const stepIds = useMemo(() => steps.map(step => step.id), [steps]);

    // --- Rename step ---
    const handleStepRename = useCallback((stepId: string, newName: string) => {
        setSteps(steps.map(s => s.id === stepId ? { ...s, name: newName } : s));
    }, [steps, setSteps]);

    // --- Handlers ---
    const handleStepDurationChange = useCallback((stepId: string, newDuration: number) => {
        let changed = false;
        const updatedSteps = steps.map((step) => {
            if (step.id === stepId) {
                // Ensure duration is positive
                const validDuration = Math.max(5, newDuration); // Min duration 5 min?
                if (step.minuteEnd - step.minuteStart !== validDuration) {
                    changed = true;
                    return {
                        ...step,
                        // We only update the duration hint here; recalculateStepTimes will fix start/end
                        minuteEnd: step.minuteStart + validDuration,
                    };
                }
            }
            return step;
        });

        if (changed) {
            console.log("Recalculating times due to duration change...");
            setSteps(recalculateStepTimes(updatedSteps));

            if(selectedStore?.id) {
                refreshSelectableResources(selectedStore.id, timezone, trpcUtils);
            }
        }
    }, [steps, setSteps, recalculateStepTimes]);


    // No handleDragStart or handleDragMove needed for simple sorting
    // We only need handleDragEnd

    const handleDragEnd = useCallback((event: DragEndEvent) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            const oldIndex = steps.findIndex((step) => step.id === active.id);
            const newIndex = steps.findIndex((step) => step.id === over.id);

            if (oldIndex !== -1 && newIndex !== -1) {
                const reorderedSteps = arrayMove(steps, oldIndex, newIndex);
                console.log("Recalculating times due to reorder...");
                setSteps(recalculateStepTimes(reorderedSteps));

                if(selectedStore?.id) {
                    refreshSelectableResources(selectedStore.id, timezone, trpcUtils);
                }
            }
        }
        // No drag preview to clear
    }, [steps, setSteps, recalculateStepTimes]);

    // --- Remove Overlap Calculation ---
    // const stepsWithOverlapInfo = useMemo(() => { ... }, [steps]); // Delete this entire useMemo block

    if (steps.length === 0) {
        return (
            <div className='border rounded-md p-4 text-center text-muted-foreground'>
                Keine Schritte hinzugefügt. Wähle eine Behandlung zu beginnen.
            </div>
        );
    }

    // Total duration for visual container height (optional)
    const totalMinutes = steps.length > 0 ? steps[steps.length - 1].minuteEnd : 0;

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            // onDragStart={handleDragStart} // Remove or simplify
            // onDragMove={handleDragMove} // Remove
            onDragEnd={handleDragEnd}
        // modifiers={[snapToGrid]} // Remove or reconsider if needed for specific effects
        >
            {/* Use SortableContext */}
            <SortableContext items={stepIds} strategy={verticalListSortingStrategy}>
                {/* Main container - adjust styling as needed */}
                <div
                    className='border rounded-md p-4 space-y-3 relative' // Use space-y for vertical spacing
                // style={{ minHeight: `${totalMinutes * GRID_PIXELS_PER_MINUTE}px` }} // Optional: Set min height based on total time
                >
                    {/* Map directly over steps, no overlap info needed */}
                    {steps.map((step) => (
                        <SortableStep
                            key={step.id}
                            id={step.id}
                            name={step.name}
                            shortDescription={step.shortDescription}
                            minuteStart={step.minuteStart}
                            minuteEnd={step.minuteEnd}
                            treatmentColor={step.treatmentColor}
                            onDurationChange={handleStepDurationChange}
                            onRename={handleStepRename}
                        />
                    ))}

                    {/* Remove dragPreview div */}
                    {/* {dragPreview && dragPreview.visible && ( ... )} */}
                </div>
            </SortableContext>
        </DndContext>
    );
};